import { createFileRoute } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PortalShell } from "@/components/portal/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";

const NAV = [
  { to: "/app/crm", label: "Übersicht" },
  { to: "/app/crm/customers", label: "Kunden" },
  { to: "/app/crm/leads", label: "Leads" },
  { to: "/app/crm/projects", label: "Projekte" },
];

export const Route = createFileRoute("/_authenticated/app/crm/leads")({
  ssr: false,
  head: () => ({ meta: [{ title: "Leads" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: LeadsPage,
});

function LeadsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", city: "", source: "" });
  const [err, setErr] = useState<string | null>(null);

  const { data: leads } = useQuery({
    queryKey: ["leads"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("id, status, first_name, last_name, email, city, source, created_at")
        .order("created_at", { ascending: false })
        .limit(200);
      if (error) throw error;
      return data;
    },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      const { data: prof } = await supabase.from("profiles").select("tenant_id").eq("id", u.user!.id).single();
      const { error } = await supabase.from("leads").insert({
        tenant_id: prof!.tenant_id, owner_user_id: u.user!.id, ...form,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      setForm({ first_name: "", last_name: "", email: "", phone: "", city: "", source: "" });
      setErr(null);
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["crm-counts"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  function onSubmit(e: FormEvent) { e.preventDefault(); createMut.mutate(); }

  return (
    <PortalShell nav={NAV}>
      <h1 className="text-2xl font-semibold mb-6">Leads</h1>

      <Card className="mb-6">
        <CardHeader><CardTitle className="text-base">Neuen Lead anlegen</CardTitle></CardHeader>
        <CardContent>
          {err && <Alert variant="destructive" className="mb-4"><AlertDescription>{err}</AlertDescription></Alert>}
          <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1"><Label>Vorname</Label><Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Nachname</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
            <div className="space-y-1"><Label>Quelle</Label><Input placeholder="Webformular, Empfehlung …" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} /></div>
            <div className="space-y-1"><Label>E-Mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1"><Label>Telefon</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div className="space-y-1"><Label>Ort</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
            <div className="sm:col-span-3"><Button type="submit" disabled={createMut.isPending}>{createMut.isPending ? "…" : "Lead anlegen"}</Button></div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Alle Leads</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Status</TableHead><TableHead>Name</TableHead><TableHead>E-Mail</TableHead><TableHead>Ort</TableHead><TableHead>Quelle</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(leads ?? []).map((l) => (
                <TableRow key={l.id}>
                  <TableCell>{l.status}</TableCell>
                  <TableCell>{`${l.first_name ?? ""} ${l.last_name ?? ""}`.trim() || "—"}</TableCell>
                  <TableCell>{l.email ?? "—"}</TableCell>
                  <TableCell>{l.city ?? "—"}</TableCell>
                  <TableCell>{l.source ?? "—"}</TableCell>
                </TableRow>
              ))}
              {(leads ?? []).length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">Noch keine Leads.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </PortalShell>
  );
}
